import {
  KycAdminEditTokenValidationResponse,
  KycFormData,
  KycFormSubmissionResponse,
  KycTokenValidationResponse,
} from '@/types';
import { apiService } from './api';

/**
 * API error response structure from Axios.
 * Used for consistent error handling across service methods.
 */
interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
    };
    status?: number;
  };
}

/**
 * Type guard to check if an error is an API error response.
 * @param error - The error to check
 * @returns True if the error has an API response structure
 */
function isApiError(error: unknown): error is ApiErrorResponse {
  return error !== null && typeof error === 'object' && 'response' in error;
}

/**
 * Extracts error message from API error response.
 * @param error - The API error response
 * @param defaultMessage - Default message if none found in response
 * @returns The extracted or default error message
 */
function extractApiErrorMessage(
  error: ApiErrorResponse,
  defaultMessage: string
): string {
  return error.response?.data?.message || defaultMessage;
}

/**
 * Creates a validation error with attached errors object.
 * @param errors - Field-specific validation errors from API
 * @returns Error with errors property attached
 */
function createValidationError(
  errors: Record<string, string[]>
): Error & { errors: Record<string, string[]> } {
  const validationError = new Error('Validation failed') as Error & {
    errors: Record<string, string[]>;
  };
  validationError.errors = errors;
  return validationError;
}

/**
 * KYC Service
 * Handles KYC-related API operations including token validation and form submissions
 */
export class KycService {
  /**
   * Validates a KYC token
   * Public endpoint - no authentication required
   *
   * @param token - The KYC token to validate
   * @returns Promise with token validation response
   */
  async validateKycToken(token: string): Promise<KycTokenValidationResponse> {
    try {
      const response = await apiService.get(`/kyc/validate-token/${token}`);
      return response.data as KycTokenValidationResponse;
    } catch (error: unknown) {
      if (isApiError(error)) {
        throw new Error(
          extractApiErrorMessage(error, 'Token validation failed')
        );
      }
      throw new Error('Network error during token validation');
    }
  }

  /**
   * Validates a KYC admin edit token and retrieves the current form data
   * Internal endpoint - accessible only within internal network
   *
   * @param token - The KYC admin edit token to validate
   * @returns Promise with token validation response including form data
   */
  async validateAdminEditToken(
    token: string
  ): Promise<KycAdminEditTokenValidationResponse> {
    try {
      const response = await apiService.get(
        `/kyc/admin-edit/validate-token/${token}`
      );
      return response.data as KycAdminEditTokenValidationResponse;
    } catch (error: unknown) {
      if (isApiError(error)) {
        throw new Error(
          extractApiErrorMessage(error, 'Admin edit token validation failed')
        );
      }
      throw new Error('Network error during admin edit token validation');
    }
  }

  /**
   * Submits a completed KYC form
   * Public endpoint - no authentication required
   *
   * @param formData - The complete KYC form data including token
   * @returns Promise with form submission response
   */
  async submitKycForm(
    formData: KycFormData
  ): Promise<KycFormSubmissionResponse> {
    try {
      const response = await apiService.post('/kyc/submit-form', formData);
      return response.data as KycFormSubmissionResponse;
    } catch (error: unknown) {
      if (isApiError(error)) {
        // Handle validation errors (422)
        if (error.response?.status === 422 && error.response?.data?.errors) {
          throw createValidationError(error.response.data.errors);
        }

        // Handle invalid token (400)
        if (error.response?.status === 400) {
          throw new Error(
            extractApiErrorMessage(error, 'Invalid or expired token')
          );
        }

        throw new Error(
          extractApiErrorMessage(error, 'Form submission failed')
        );
      }
      throw new Error('Network error during form submission');
    }
  }

  /**
   * Submits a KYC form via admin edit
   * Internal endpoint - accessible only within internal network
   *
   * @param formData - The complete KYC form data including token
   * @returns Promise with form submission response
   */
  async submitAdminEditKycForm(
    formData: KycFormData
  ): Promise<KycFormSubmissionResponse> {
    try {
      const response = await apiService.post(
        '/kyc/admin-edit/submit-form',
        formData
      );
      return response.data as KycFormSubmissionResponse;
    } catch (error: unknown) {
      if (isApiError(error)) {
        // Handle validation errors (422)
        if (error.response?.status === 422 && error.response?.data?.errors) {
          throw createValidationError(error.response.data.errors);
        }

        // Handle invalid token (400)
        if (error.response?.status === 400) {
          throw new Error(
            extractApiErrorMessage(error, 'Invalid or expired token')
          );
        }

        throw new Error(
          extractApiErrorMessage(error, 'Admin edit form submission failed')
        );
      }
      throw new Error('Network error during admin edit form submission');
    }
  }
}

// Export singleton instance
export const kycService = new KycService();
